import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { ExpandMore, NavigateNext } from '@mui/icons-material';
import BaseModal from 'components/BaseModal';
import FolderModal from '../FolderModal';
import FolderCard from '../FolderCard';
import { useStyles } from 'styles/components/creationAccordion';

const Props = {
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  activeFolder: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  creationsListComponent: PropTypes.node,
  onEnter: PropTypes.func.isRequired,
  onOut: PropTypes.func.isRequired,
  onOptionClick: PropTypes.func.isRequired,
  modalOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  onModalActionClick: PropTypes.func.isRequired,
  onDeleteModalActionClick: PropTypes.func.isRequired,
};

export default function FoldersAccordion({
  folders,
  activeFolder,
  creationsListComponent,
  onEnter,
  onOut,
  onOptionClick,
  modalOpen,
  onModalClose,
  onModalActionClick,
  onDeleteModalActionClick,
}) {
  const classes = useStyles();
  const [currentFolder, setCurrentFolder] = useState();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  function handleOptionClick(option, folder) {
    switch (option.key) {
      case 'rename':
        setCurrentFolder(folder);
        break;
      case 'delete':
        setCurrentFolder(folder);
        setDeleteModalOpen(true);
        break;
    }

    onOptionClick(option);
  }

  function handleModalActionClick(folderName) {
    onModalActionClick(folderName, currentFolder);

    setCurrentFolder();
  }

  function handleModalClose() {
    setCurrentFolder();
    onModalClose();
  }

  function handleDeleteModalActionClick() {
    onDeleteModalActionClick(currentFolder);

    setCurrentFolder();
    setDeleteModalOpen(false);
  }

  function handleDeleteModalClose() {
    setCurrentFolder();
    setDeleteModalOpen(false);
  }

  function handleOutClick(event) {
    event.stopPropagation();

    onOut();
  }

  function getContentComponent() {
    if (creationsListComponent) {
      return creationsListComponent;
    }

    if (!folders.length) {
      return (
        <Typography variant="body2" color="textSecondary">
          No items
        </Typography>
      );
    }

    return (
      <Grid container={true} spacing={3}>
        {folders.map((folder) => (
          <Grid key={folder.id} item={true} xs={12} sm={6} md={3}>
            <FolderCard
              folder={folder}
              onEnter={() => onEnter(folder)}
              options={[
                { key: 'rename', label: 'Rename' },
                { key: 'delete', label: 'Delete' },
              ]}
              onOptionClick={(option) => handleOptionClick(option, folder)}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      {!!folders.length && (
        <Accordion
          classes={{ root: classes.panel, expanded: classes.panelExpanded }}
          defaultExpanded={true}
        >
          <AccordionSummary
            classes={{ root: classes.panelSummary, content: classes.panelSummaryContent }}
            expandIcon={<ExpandMore />}
            aria-label="Folders Accordion"
          >
            {!activeFolder ? (
              <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
                <Typography color="textPrimary">
                  <b>Folders</b>
                </Typography>
              </Breadcrumbs>
            ) : (
              <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
                <Link color="inherit" onClick={handleOutClick} underline="hover">
                  <b>Folders</b>
                </Link>
                <Typography color="textPrimary">
                  <b>{activeFolder.name}</b>
                </Typography>
              </Breadcrumbs>
            )}
          </AccordionSummary>

          <div role="region" aria-label="Folders Section">
            <AccordionDetails className={classes.panelDetails}>
              {getContentComponent()}
            </AccordionDetails>
          </div>
        </Accordion>
      )}

      <FolderModal
        open={modalOpen}
        folder={currentFolder}
        onActionClick={handleModalActionClick}
        onClose={handleModalClose}
      />

      <BaseModal
        open={deleteModalOpen}
        title={
          <b>
            Delete the folder <i>{currentFolder?.name}</i>?
          </b>
        }
        actionText="Delete"
        onActionClick={handleDeleteModalActionClick}
        onClose={handleDeleteModalClose}
      />
    </>
  );
}

FoldersAccordion.propTypes = Props;
